/*
Purpose: list details of report with use datatable
Developer: BY KTH
Date:31/08/2026
Project: TCMS
*/

import { useState, useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import { Button as Buttonmui } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { ToastContainer, toast } from "react-toastify";

import axios from "axios";
import { security_key, BASE_URL } from "../global";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar, useGridApiRef } from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import Moment from "moment";
import Select from "react-select";

// Shared "Set as Paid" modal + formik, extracted from the student payment
// details page so both pages use the exact same logic/markup instead of
// two copies drifting apart.
import OfflineSetAsPaidModal from "./OfflineSetAsPaidModal";

function ReportFinanceFailedPayments() {
  const [loading, setLoading] = useState(false);

  const [records, setRecords] = useState([]);
  const [retryingKeys, setRetryingKeys] = useState([]);
  const [sendingKeys, setSendingKeys] = useState([]);
  const [markingPaidKeys, setMarkingPaidKeys] = useState([]);

  // --- Offline "Set as Paid" modal state (shared component) ---
  const [showOfflinePaidModal, setShowOfflinePaidModal] = useState(false);
  const [offlinePaidData, setOfflinePaidData] = useState(null);
  const [offlinePaidStudent, setOfflinePaidStudent] = useState(null);
  const [offlineModesOptions, setOfflineModesOptions] = useState([]);

  // --- Filters ---
  const [filterStudent, setFilterStudent] = useState(null);
  const [filterPaymentMethod, setFilterPaymentMethod] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // Student options loaded once, independent of the failed-payment filters
  const [studentOptions, setStudentOptions] = useState([]);

  const paymentMethodOptions = [
    { value: "1", label: "Online" },
    { value: "2", label: "Offline" },
  ];

  const fetchStudentOptions = async () => {
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getStudentListForCentre",
        fk_cent_key: sessionStorage.getItem("key"),
      });

      if (response.data.success === true) {
        // NOTE: assumes getStudentListForCentre also returns balance_wallet
        // per student (needed by the "pay from wallet" toggle in the shared
        // modal). If it doesn't, add it on the backend or swap this for a
        // dedicated "getStudentWalletBalance" call in handleSetAsPaid below.
        const options = response.data.data.map((s) => ({
          value: s.pk_student_key,
          label: `${s.st_first_name || ""} ${s.st_surname || ""}`.trim() || "Unknown",
          st_first_name: s.st_first_name,
          st_surname: s.st_surname,
          balance_wallet: s.balance_wallet || 0,
        }));
        setStudentOptions(options);
      }
    } catch (error) {
      console.error("Error fetching student list:", error);
    }
  };

  // TODO: confirm the real API mode name for listing a centre's offline
  // payment modes (copied pattern from fetchStudentOptions - the original
  // page that owned the modal must already call something like this to
  // populate OfflineModesOptions; point this at that same mode).
  const fetchOfflineModesOptions = async () => {
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getOfflinePaymentModeCenter",
        fk_cent_key: sessionStorage.getItem("key"),
      });

      if (response.data.success === true) {
         const formattedOptions = response.data.data.map(option => ({
          value: option.pk_cpm_key,
          label: option.pm_name
        }));
        setOfflineModesOptions(formattedOptions);
      }
    } catch (error) {
      console.error("Error fetching payment modes:", error);
    }
  };

  const fetchData = async (filters = {}) => {
    setLoading(true);
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getFailedOnlinePaymentReport",
        fk_cent_key: sessionStorage.getItem("key"),
        fk_student_key: filters.fk_student_key || "",
        pp_pay_method: filters.pp_pay_method || "",
        ps_date_from: filters.ps_date_from || "",
        ps_date_to: filters.ps_date_to || "",
      });

      if (response.data.success === true) {
        setRecords(response.data.data);
      } else {
        setRecords([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchData({
      fk_student_key: filterStudent?.value || "",
      pp_pay_method: filterPaymentMethod?.value || "",
      ps_date_from: filterStartDate,
      ps_date_to: filterEndDate,
    });
  };

  useEffect(() => {
    fetchStudentOptions();
    fetchOfflineModesOptions();
  }, []);
  

  useEffect(() => {
    handleApplyFilters();
  }, [filterStudent, filterPaymentMethod, filterStartDate, filterEndDate]);

  const handleClearFilters = () => {
    setFilterStudent(null);
    setFilterPaymentMethod(null);
    setFilterStartDate("");
    setFilterEndDate("");
    fetchData({});
  };

//   const handleManualRetry = async (pk_ps_key) => {
//     setRetryingKeys((prev) => [...prev, pk_ps_key]);
//     try {
//       // FIX: original code called `${API_BASE}/retry_payment.php`, but
//       // API_BASE isn't defined anywhere on this page - it looks like a
//       // leftover from a different project. Pointing this at the same
//       // api_index.php pattern every other call on this page uses.
//       // TODO: confirm "retryFailedOnlinePayment" is the real mode name.
//       const res = await axios.post(BASE_URL + "API/api_index.php", {
//         security_key: security_key,
//         mode: "retryOnlinePaymentByStudentCenter",
//         pk_ps_key,
//       });
//       if (res.data.success) {
//         toast.success("Payment retried successfully");
//       } else {
//         toast.error(res.data.message || "Retry failed");
//       }
//       // FIX: original called an undefined refreshFailedPaymentGrid(); use
//       // the grid's own reload function instead.
//       handleApplyFilters();
//     } catch (e) {
//       toast.error("Something went wrong while retrying");
//     } finally {
//       setRetryingKeys((prev) => prev.filter((k) => k !== pk_ps_key));
//     }
//   };

  const [isRetrying, setIsRetrying] = useState(false);

  const handleManualRetry = async (pk_ps_key) => {
    setIsRetrying(true);
    setRetryingKeys((prev) => [...prev, pk_ps_key]);
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "retryOnlinePaymentByStudentCenter",
        pk_ps_key: pk_ps_key,
      });

      // dispatcher always returns success: true and wraps the real
      // outcome in data.res / data.message — branch on res, not success.
      const { res, message } = response.data.data || {};

      switch (res) {
        case 1:
          toast.success(message || "Payment successful.", { autoClose: 3000 });
          // refresh the row/list so status reflects paid
          //onSuccess && onSuccess();
          break;
        case 2:
          toast.info(message || "This invoice is already paid.", { autoClose: 3000 });
          //onSuccess && onSuccess();
          break;
        case 0:
          toast.error(message || "Payment failed. Please try again.", { autoClose: 4000 });
          break;
        case -1:
          toast.error(message || "This invoice is not eligible for retry.", { autoClose: 4000 });
          break;
        default:
          toast.error("Unable to process payment. Try again later.", { autoClose: 3000 });
      }
    } catch (error) {
      console.error("Error retrying online payment:", error);
      //toast.error("Unable to process payment. Try again later.", { autoClose: 3000 });
    } finally {
      setIsRetrying(false);
      setRetryingKeys((prev) => prev.filter((k) => k !== pk_ps_key));
      handleApplyFilters();
    }
  };


  const handleManualEmail = async (pk_ps_key, row) => {
    setSendingKeys((prev) => [...prev, pk_ps_key]);

    const failed_reason =parseInt(row.ps_status) === 0 &&
          row.ps_payment_session_id &&
          row.ps_payment_session_status !== "canceled" &&
          row.ps_payment_session_status !== "expired"
            ? "Waiting for payment link response"
            : row.ps_stripe_status
            ? row.ps_stripe_status
            : parseInt(row.ps_status) === 0 &&
              row.ps_payment_session_id &&
              (row.ps_payment_session_status == "canceled" ||
                row.ps_payment_session_status == "expired")
            ? "Payment link " + row.ps_payment_session_status
            : row.pp_pay_method == "2"
            ? "Admin did not process offline payment"
            : row.pp_pay_method == "1"
            ? "Automation payment not processed yet"
            : "";

    try {
      const response = await axios.post(
        BASE_URL + "API/api_index.php",
        {
          security_key: security_key,
          mode: "manuallySentFailedPaymentEmail",
          fk_ps_key: pk_ps_key,
          failure_reason: failed_reason,
        }
      );

      if (response.data?.success === true) {
        toast.success(
          "Failed payment email sent successfully.",
          { autoClose: 3000 }
        );
      } else {
        toast.error(
          response.data?.error || "Failed to send payment email. Please try again.",
          { autoClose: 4000 }
        );
      }

    } catch (error) {
      console.error("Error sending failed payment email:", error);

      toast.error(
        error.response?.data?.error ||
        "Unable to send failed payment email. Please try again later.",
        { autoClose: 4000 }
      );

    } finally {
      setSendingKeys((prev) => prev.filter((k) => k !== pk_ps_key));
      handleApplyFilters();
    }
  };

  // Wires this page's "Set as Paid" button to the shared modal component.
  const handleSetAsPaid = (row) => {
    setOfflinePaidData(row);

    // Row already carries the student's name; wallet balance comes from
    // the once-loaded studentOptions list (see NOTE in fetchStudentOptions).
    const matchedStudent = studentOptions.find((s) => s.value === row.fk_student_key);
    setOfflinePaidStudent({
      st_first_name: row.st_first_name || matchedStudent?.st_first_name,
      st_surname: row.st_surname || matchedStudent?.st_surname,
      balance_wallet: matchedStudent?.balance_wallet || 0,
    });

    setShowOfflinePaidModal(true);
  };

  const closeOfflinePaidModal = () => {
    setShowOfflinePaidModal(false);
    setOfflinePaidData(null);
    setOfflinePaidStudent(null);
  };

  const failedDataGridApiRef = useGridApiRef();

  /**
   * Positions BOTH the sticky header cells and the sticky body cells
   * for every column carrying the `freeze` class, based on cumulative
   * offsetWidth of the preceding freeze columns.
   */
  function freezeGridColumns(gridApi, params) {
    if (!gridApi) return;
    const gridElement = gridApi.rootElementRef?.current;
    if (!gridElement) return;

    const scrollLeft = params?.left ?? 0;

    const headerCells = gridElement.querySelectorAll(".MuiDataGrid-columnHeader.freeze");
    headerCells.forEach((cell) => {
      cell.style.transform = `translate3d(${scrollLeft}px, 0, 0)`;
    });

    const offsets = {};
    let offset = 0;
    headerCells.forEach((cell) => {
      const field = cell.getAttribute("data-field");
      offsets[field] = offset;
      offset += cell.offsetWidth;
    });

    const bodyCells = gridElement.querySelectorAll(".MuiDataGrid-cell.freeze");
    bodyCells.forEach((cell) => {
      const field = cell.getAttribute("data-field");
      if (offsets[field] !== undefined) {
        cell.style.left = `${offsets[field]}px`;
      }
    });
  }

  useEffect(() => {
    const gridApi = failedDataGridApiRef.current;
    if (!gridApi) return;

    const unsubscribeScroll = gridApi.subscribeEvent("scrollPositionChange", (params) => {
      freezeGridColumns(gridApi, params);
    });
    const unsubscribeRows = gridApi.subscribeEvent("rowsSet", () => {
      requestAnimationFrame(() => freezeGridColumns(gridApi, { left: 0 }));
    });

    return () => {
      unsubscribeScroll();
      unsubscribeRows();
    };
  }, [failedDataGridApiRef]);

  const gridRows = useMemo(() => records.map((row, index) => ({ ...row, id: `row-${index}` })), [records]);

  const columns = useMemo(
    () => [
      {
        headerName: "Student",
        field: "student",
        headerClassName: "super-app-theme--header freeze",
        cellClassName: "cell freeze",
        minWidth: 150,
        valueGetter: (params) => `${params.row.st_first_name || ""} ${params.row.st_surname || ""}`.trim() || "-",
      },
      {
        headerName: "Due Date",
        field: "ps_date",
        headerClassName: "super-app-theme--header freeze",
        cellClassName: "cell freeze",
        minWidth: 110,
        valueGetter: (params) =>
          params.row.ps_date ? Moment(params.row.ps_date, "YYYY-MM-DD").format("DD-MM-YYYY") : "-",
      },
      {
        headerName:
          "Amount(" +
          (+sessionStorage.getItem("currency_code") != null && sessionStorage.getItem("currency_code") != ""
            ? sessionStorage.getItem("currency_code")
            : "SGD") +
          ")",
        field: "ps_amount",
        headerClassName: "super-app-theme--header freeze",
        cellClassName: "cell freeze",
        minWidth: 100,
        valueGetter: (params) =>
         params.row.ps_amount || "0.00",
      },
      {
        headerName:
          "Due Amount(" +
          (+sessionStorage.getItem("currency_code") != null && sessionStorage.getItem("currency_code") != ""
            ? sessionStorage.getItem("currency_code")
            : "SGD") +
          ")",
        field: "ps_due_amount",
        headerClassName: "super-app-theme--header freeze",
        cellClassName: "cell freeze left-150",
        minWidth: 100,
        valueGetter: (params) => {
          return `${
            Number(params?.row?.ps_status) == 0 && Number(params?.row?.is_part_payment) == 0
              ? params.row.ps_amount
              : params.row.ps_due_amount && params.row.ps_due_amount != "" && params.row.rp_type != 4
              ? params.row.ps_due_amount
              : "0.00"
          }`;
        },
      },
      {
        headerName: "Session Name",
        field: "sest_name",
        headerClassName: "super-app-theme--header",
        minWidth: 200,
        valueGetter: (params) => params.row.sest_name || "-",
      },
      {
        headerName: "Product Name",
        field: "pp_name",
        headerClassName: "super-app-theme--header",
        minWidth: 200,
        valueGetter: (params) => params.row.pp_name || "-",
      },
      {
        headerName: "Payment Method",
        field: "pp_pay_method",
        headerClassName: "super-app-theme--header",
        minWidth: 110,
        valueGetter: (params) =>
          params.row.pp_pay_method == "2" ? "Offline" : params.row.pp_pay_method == "1" ? "Online" : "-",
      },
      {
        headerName: "Failure Reason",
        field: "ps_stripe_status",
        headerClassName: "super-app-theme--header",
        minWidth: 250,
        valueGetter: (params) =>
          parseInt(params.row.ps_status) === 0 &&
          params.row.ps_payment_session_id &&
          params.row.ps_payment_session_status !== "canceled" &&
          params.row.ps_payment_session_status !== "expired"
            ? "Waiting for payment link response"
            : params.row.ps_stripe_status
            ? params.row.ps_stripe_status
            : parseInt(params.row.ps_status) === 0 &&
              params.row.ps_payment_session_id &&
              (params.row.ps_payment_session_status == "canceled" ||
                params.row.ps_payment_session_status == "expired")
            ? "Payment link " + params.row.ps_payment_session_status
            : params.row.pp_pay_method == "2"
            ? "Admin did not process offline payment"
            : params.row.pp_pay_method == "1"
            ? "Automation payment not processed yet"
            : "",
        renderCell: (params) => {
          const value =
            parseInt(params.row.ps_status) === 0 &&
            params.row.ps_payment_session_id &&
            params.row.ps_payment_session_status !== "canceled" &&
            params.row.ps_payment_session_status !== "expired"
              ? "Waiting for payment link response"
              : params.row.ps_stripe_status
              ? params.row.ps_stripe_status
              : parseInt(params.row.ps_status) === 0 &&
                params.row.ps_payment_session_id &&
                (params.row.ps_payment_session_status == "canceled" ||
                  params.row.ps_payment_session_status == "expired")
              ? "Payment link " + params.row.ps_payment_session_status
              : params.row.pp_pay_method == "2"
              ? "Admin did not process offline payment"
              : params.row.pp_pay_method == "1"
              ? "Automation payment not processed yet"
              : "";

          return (
            <Tooltip title={value || ""} arrow>
              <div className="text-danger text-wrap">{value}</div>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Last Retry Date",
        field: "ps_last_try_payment_date",
        headerClassName: "super-app-theme--header",
        minWidth: 130,
        valueGetter: (params) =>
          params.row.ps_next_try_payment_date
            ? Moment(params.row.ps_next_try_payment_date, "YYYY-MM-DD").subtract(1, "day").format("DD-MM-YYYY")
            : parseInt(params.row.ps_status) === 0 &&
              params.row.ps_payment_session_id &&
              (params.row.ps_payment_session_status == "canceled" || params.row.ps_payment_session_status == "expired")
            ? Moment(params.row.updated_on, "YYYY-MM-DD").format("DD-MM-YYYY")
            : "-",
      },
      {
        headerName: "Next Retry Date",
        field: "ps_next_try_payment_date",
        headerClassName: "super-app-theme--header",
        minWidth: 130,
        valueGetter: (params) =>
          params.row.ps_next_try_payment_date
            ? Moment(params.row.ps_next_try_payment_date, "YYYY-MM-DD").format("DD-MM-YYYY")
            : "-",
      },
      {
        headerName: "Email Status",
        field: "Email Status",
        headerClassName: "super-app-theme--header",
        minWidth: 100,
        valueGetter: (params) => {
          return params.row.email_status == "1"
            ? "Resend Email"
            : "Send Email";
        },
        renderCell: (params) => {
          const row = params.row;

          // if (row.pp_pay_method == "1") {
            return (
              <><Buttonmui
                variant="outlined"
                style={{
                  marginRight: "0.5rem",
                  marginTop: "auto",
                  marginBottom: "auto",
                  color: "white",
                  backgroundColor:  row.email_status == "1" ? "#28a745" : "#277ca0", // Green background for sent emails, light gray for unsent
                  border:  row.email_status == "1" ? "1px solid #28a745" : "1px solid #277ca0",
                  fontSize: "10px",
                  textTransform: "none",
                  minWidth: "max-content",
                  width: "max-content",
                  padding: "4px 6px",
                }}
                disabled={sendingKeys.includes(row.pk_ps_key)}
                onClick={() => handleManualEmail(row.pk_ps_key, row)}
              >
                {sendingKeys.includes(row.pk_ps_key) ? "sending..." : row.email_status == "1" ? "Resend Email" : "Send Email"}
              </Buttonmui></>
            );
          //}


          // return null;
        },
      },
      {
        headerName: "Action",
        field: "action",
        headerClassName: "super-app-theme--header",
        minWidth: 180,
        disableExport: true,
        disableColumnFilter: false,
        disableColumnMenu: false,
        filterable: false,
        sortable: false,
        renderCell: (params) => {
          const row = params.row;

          if (row.pp_pay_method == "1") {
            return (
              <><Buttonmui
                variant="outlined-red"
                className="primary-blue my-2"
                disabled={retryingKeys.includes(row.pk_ps_key)}
                onClick={() => handleManualRetry(row.pk_ps_key)}
              >
                {retryingKeys.includes(row.pk_ps_key) ? "Retrying..." : "Retry"}
              </Buttonmui>
              <Buttonmui
                variant="outlined-red"
                className="primary-blue my-2"
                disabled={markingPaidKeys.includes(row.pk_ps_key)}
                onClick={() => handleSetAsPaid(row)}
              >
                {markingPaidKeys.includes(row.pk_ps_key) ? "Saving..." : "Set as Paid"}
              </Buttonmui></>
            );
          }

          if (row.pp_pay_method == "2" || row.pp_pay_method == "1") {
            return (
              <Buttonmui
                variant="outlined-red"
                className="primary-blue my-2"
                disabled={markingPaidKeys.includes(row.pk_ps_key)}
                onClick={() => handleSetAsPaid(row)}
              >
                {markingPaidKeys.includes(row.pk_ps_key) ? "Saving..." : "Set as Paid"}
              </Buttonmui>
            );
          }

          return null;
        },
      },
    ],
    
    [retryingKeys, markingPaidKeys, studentOptions, sendingKeys, filterStudent, filterPaymentMethod, filterStartDate, filterEndDate] // rebuild columns when these actually change
  );

  return (
    <div className="">
      <ToastContainer position="top-center" />
      {/* --------------Page Start--------------- */}

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <h5 className="page-title"> Failed Payment Reports</h5>

          <div role="presentation">
            <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
              <NavLink underline="hover" color="inherit" to="/calendar">
                {" "}
                Home
              </NavLink>
              <NavLink underline="hover" color="inherit" to="/reports">
                {" "}
                Reports
              </NavLink>
              <Typography color="text.primary">Failed Payment Reports </Typography>
            </Breadcrumbs>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2"></div>
      </div>
      {/* --------------Section Start--------------- */}

      <Card className="card my-card mb-3">
        <Card.Body>
          {/* -------------Content Start--------------- */}
          <Row className="mb-3 align-items-end g-2">
            <Col xs={12} md={3}>
              <label className="form-label small mb-1">Student</label>
              <Select
                options={studentOptions}
                value={filterStudent}
                onChange={setFilterStudent}
                isClearable
                placeholder="All students"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>

            <Col xs={12} md={3}>
              <label className="form-label small mb-1">Payment Method</label>
              <Select
                options={paymentMethodOptions}
                value={filterPaymentMethod}
                onChange={setFilterPaymentMethod}
                isClearable
                placeholder="All methods"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>

            <Col xs={6} md={2}>
              <label className="form-label small mb-1">From Date</label>
              <input
                type="date"
                className="form-control mb-0"
                value={filterStartDate}
                max={filterEndDate || undefined}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
            </Col>

            <Col xs={6} md={2}>
              <label className="form-label small mb-1">To Date</label>
              <input
                type="date"
                className="form-control mb-0"
                value={filterEndDate}
                min={filterStartDate || undefined}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
            </Col>

            <Col xs={12} md={2} className="d-flex gap-2">
              <Buttonmui variant="outlined" className="w-50" onClick={handleClearFilters} disabled={loading}>
                Clear
              </Buttonmui>
            </Col>
          </Row>

          <Row>
            <Col xs="12">
              <div className="">
                <Row className="justify-content-center">
                  {/* --------------Table Content Start--------------- */}
                  <Col sm={12}>
                    <Box
                      sx={{
                        height: "fit-content",
                        width: "100%",
                        "& .actions": {
                          color: "text.secondary",
                        },
                        "& .textPrimary": {
                          color: "text.primary",
                        },
                        "& .super-app-theme--header": {
                          backgroundColor: "#f4f4f4",
                        },
                        "& .MuiDataGrid-row": {
                          border: "1px solid #eff0f2",
                        },
                        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                          outline: "none !important",
                        },
                        "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus": {
                          outline: "none !important",
                        },
                        "& .MuiDataGrid-columnHeader.freeze": {
                          position: "relative",
                          zIndex: 3,
                          backgroundColor: "#f4f4f4",
                        },
                        "& .MuiDataGrid-cell.freeze": {
                          position: "sticky",
                          zIndex: 2,
                          backgroundColor: "#fff",
                        },
                        "& .MuiDataGrid-cell.freeze:last-of-type, & .MuiDataGrid-columnHeader.freeze:last-of-type": {
                          boxShadow: "2px 0 4px -2px rgba(0,0,0,0.25)",
                        },
                      }}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <DataGrid
                        className={"freeze-list"}
                        disableVirtualization={true}
                        sx={{
                          ".MuiDataGrid-cell:focus": {
                            outline: "none",
                          },
                          "& .MuiDataGrid-row:hover": {
                            cursor: "pointer",
                          },
                        }}
                        apiRef={failedDataGridApiRef}
                        autoHeight
                        slots={{
                          loadingOverlay: LinearProgress,
                          toolbar: GridToolbar,
                        }}
                        slotProps={{
                          toolbar: {
                            csvOptions: { fileName: "FailedPaymentsReport" },
                            printOptions: {
                              pageStyle: `@media print {
                                              @page {
                                                  size: A4 landscape;
                                                  margin: 0;
                                              }
                                              }`,
                              disableToolbarButton: true,
                              hideFooter: true,
                              hideToolbar: true,
                              fileName: "StudentOnlinePaymentStatusReport",
                            },
                            gridRows,
                            showQuickFilter: true,
                          },
                        }}
                        initialState={{
                          ...gridRows.initialState,
                          pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        showQuickFilter={true}
                        pageSizeOptions={[10, 25, 50, 100]}
                        loading={loading}
                        rows={gridRows}
                        columns={columns}
                        rowHeight={60}
                        density="compact"
                        disableRowSelectionOnClick
                        showCellVerticalBorder={true}
                        showColumnVerticalBorder={true}
                      />
                    </Box>
                  </Col>
                  {/* --------------Table Content End--------------- */}
                </Row>
              </div>
            </Col>
          </Row>

          {/* --------------Content End--------------- */}
        </Card.Body>
      </Card>

      {/* --------------Section End--------------- */}

      {/* Shared "Set as Paid" modal - same component/markup used on the
          student payment details page, driven by whichever row was clicked. */}
      <OfflineSetAsPaidModal
        show={showOfflinePaidModal}
        onHide={closeOfflinePaidModal}
        offlinePaidData={offlinePaidData}
        setOfflinePaidData={setOfflinePaidData}
        studentData={offlinePaidStudent}
        offlineModesOptions={offlineModesOptions}
        onSuccess={handleApplyFilters}
      />

      {/* --------------Page End--------------- */}
    </div>
  );
}

export default ReportFinanceFailedPayments;